import { useTranslation } from '@/hooks/useTranslation';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import type { PartSummary } from '../utils/partGroups';

// ---------- Props ----------

interface PartPickerProps {
  parts: PartSummary[];
  /** `null` means the "All parts" pseudo-selection; only reachable when `allowAll`. */
  selectedPartId: string | null;
  onSelect(partId: string | null): void;
  /** Adds the dashed "All parts" card. Default `false`. */
  allowAll?: boolean;
  /** Disables a card whose part is fully labeled. Default `false`. */
  disableCompleted?: boolean;
  /** Extra classes on the grid wrapper, so each surface controls its own padding. */
  className?: string;
}

/**
 * Shared by both card flavours: the card shell, its hover hairline, focus ring and disabled look.
 * A structural button (a card that selects a part), so not a `Button` variant.
 */
const CARD_CLASSES =
  'group w-full bg-card rounded-xl p-4 flex flex-col items-start gap-2 text-left text-sm font-medium transition-colors hover:bg-primary/5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50';

/** Selected state — semantic tokens only, so it holds in both themes. */
const SELECTED_CLASSES = 'border-primary ring-1 ring-primary/40';

// ---------- Part card ----------

interface PartCardProps {
  part: PartSummary;
  selected: boolean;
  disabled?: boolean;
  onSelect(partId: string): void;
}

function PartCard({ part, selected, disabled = false, onSelect }: PartCardProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      data-testid={`part-card-${part.partId}`}
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelect(part.partId)}
      className={cn(
        CARD_CLASSES,
        'border border-border hover:border-primary/40',
        selected && SELECTED_CLASSES
      )}
    >
      <div
        className={cn(
          'h-0.5 w-full rounded-full bg-primary opacity-0 transition-opacity',
          selected && 'opacity-100',
          !disabled && 'group-hover:opacity-100'
        )}
      />
      <span className="text-lg font-bold tabular-nums text-foreground">
        {t('labeling.partPicker.partLabel', { partId: part.partId })}
      </span>
      <span className="text-xs text-muted-foreground">
        {t('labeling.partPicker.imageCount', { count: part.imageCount })}
      </span>
      <span
        data-testid={`part-progress-${part.partId}`}
        className="text-xs tabular-nums text-muted-foreground"
      >
        {part.labeledCount} / {part.imageCount}
      </span>
      {disabled && (
        <span className="mt-auto text-xs font-medium text-muted-foreground">
          {t('labeling.partPicker.fullyLabeled')}
        </span>
      )}
    </button>
  );
}

// ---------- All-parts card ----------

interface AllPartsCardProps {
  selected: boolean;
  /** Aggregate over every part — computed by PartPicker, displayed here. */
  totals: { imageCount: number; labeledCount: number };
  onSelect(): void;
}

function AllPartsCard({ selected, totals, onSelect }: AllPartsCardProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      data-testid="part-card-all"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        CARD_CLASSES,
        'border border-dashed border-border hover:border-primary/40',
        selected && SELECTED_CLASSES
      )}
    >
      <div
        className={cn(
          'h-0.5 w-full rounded-full bg-primary opacity-0 transition-opacity group-hover:opacity-100',
          selected && 'opacity-100'
        )}
      />
      <span className="text-lg font-bold text-foreground">{t('labeling.partPicker.allParts')}</span>
      <span className="text-xs text-muted-foreground">{t('labeling.partPicker.allPartsHint')}</span>
      <span data-testid="part-progress-all" className="text-xs tabular-nums text-muted-foreground">
        {totals.labeledCount} / {totals.imageCount}
      </span>
    </button>
  );
}

// ---------- Main component ----------

/**
 * The one part-picking grid, rendered both inside the toolbar sheet and inline in Dichotomous
 * mode. Purely presentational: it owns no state and no context, only the cards and the empty
 * state — every surface brings its own heading and padding.
 */
export function PartPicker({
  parts,
  selectedPartId,
  onSelect,
  allowAll = false,
  disableCompleted = false,
  className,
}: PartPickerProps) {
  const { t } = useTranslation();

  // Nothing to pick between: show the empty message alone, never a lone "All parts" card that
  // would select an empty set.
  if (parts.length === 0) {
    return (
      <div data-testid="part-picker" className={className}>
        <EmptyState title={t('labeling.partPicker.empty')} />
      </div>
    );
  }

  const totals = parts.reduce(
    (acc, part) => ({
      imageCount: acc.imageCount + part.imageCount,
      labeledCount: acc.labeledCount + part.labeledCount,
    }),
    { imageCount: 0, labeledCount: 0 }
  );

  return (
    <div
      data-testid="part-picker"
      className={cn('grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3', className)}
    >
      {allowAll && (
        <AllPartsCard
          selected={selectedPartId === null}
          totals={totals}
          onSelect={() => onSelect(null)}
        />
      )}
      {parts.map((part) => (
        <PartCard
          key={part.partId}
          part={part}
          selected={selectedPartId === part.partId}
          disabled={disableCompleted && part.labeledCount === part.imageCount}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
